// CRM Mobile v3 — estilo WhatsApp Business con identidad Limpack.
// 8 vistas: inbox (con pineados + tabs + bottom nav) → chat (con auto bubbles,
// reply quote, AI replies) → quick actions sheet → drawer 360 → nuevo chat wizard
// → stats tab. Todo usa componentes compartidos de components/crm/sharedV3.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import {
  getConversaciones, getMensajes, marcarComoLeida, enviarMensaje,
  suscribirInboxEnVivo, suscribirMensajesEnVivo,
  getTemplatesAprobados, iniciarConversacionConTemplate,
  buscarClientesParaCRM, subirAdjuntoCRM, listarContactos,
} from '../../lib/crmApi'
import { pushSupported, pushSubscribed, pushSubscribe, pushUnsubscribe, pushTest, pushReset } from '../../lib/pushNotifications'
import {
  C, Icon, classNombre, gradAvatar,
  AutoBubble, AutomationsTimeline, CustomerDrawer,
  QuickActionsSheet, AISuggestionsBar,
} from '../../components/crm/sharedV3'

export default function CRMMobile() {
  const [tab, setTab] = useState('chats') // chats | contactos | broadcast | stats
  const [convs, setConvs] = useState([])
  const [activa, setActiva] = useState(null)
  const [busqueda, setBusqueda] = useState('')
  const [loading, setLoading] = useState(true)
  const [pushOn, setPushOn] = useState(false)
  const [filtro, setFiltro] = useState('todos')
  const [filtCanal, setFiltCanal] = useState('todos') // todos | whatsapp | instagram
  const [showNuevoChat, setShowNuevoChat] = useState(false)

  useEffect(() => {
    document.body.classList.add('crm-mobile-mode')
    return () => document.body.classList.remove('crm-mobile-mode')
  }, [])

  // Notificaciones por defecto ON. Auto-suscribir si ya hay permiso del browser.
  // Si no hay sesión cuando intenta suscribirse, reintenta hasta 2s (sesión hidrata).
  useEffect(() => {
    if (!pushSupported()) return
    let cancel = false
    ;(async () => {
      const yaSub = await pushSubscribed()
      if (cancel) return
      if (yaSub) { setPushOn(true); return }
      if (Notification.permission === 'granted') {
        // Esperar a que la sesión Supabase esté hidratada
        for (let i = 0; i < 5; i++) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) break
          await new Promise(r => setTimeout(r, 400))
        }
        if (cancel) return
        try { await pushSubscribe(); setPushOn(true) }
        catch (e) { console.warn('Auto-subscribe mobile falló:', e?.message || e) }
      }
    })()
    return () => { cancel = true }
  }, [])

  async function cargar() {
    setLoading(true)
    const list = await getConversaciones({ canal: 'todos', estado: 'todos', soloNoLeidos: false, q: busqueda })
    setConvs(list || [])
    setLoading(false)
  }
  useEffect(() => { cargar() }, [busqueda])
  useEffect(() => {
    const ch = suscribirInboxEnVivo(() => cargar())
    return () => { try { supabase.removeChannel(ch) } catch {} }
  }, [busqueda])

  async function abrirConv(c) {
    setActiva(c)
    if (c.unread_count > 0) marcarComoLeida(c.id).then(() => cargar())
    // History push para que el "atrás" de Android cierre el chat (no la página)
    try { window.history.pushState({ crmChat: c.id }, '') } catch {}
  }

  // Atrás de Android / browser → cerrar chat, no salir de la página
  useEffect(() => {
    const onPop = () => { setActiva(null) }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // iOS Safari NO soporta interactive-widget=resizes-content. Cuando se abre el teclado,
  // visualViewport.height se achica. Ajustamos el wrap a esa altura para que el composer suba.
  useEffect(() => {
    if (!window.visualViewport) return
    const vv = window.visualViewport
    function ajustar() {
      document.documentElement.style.setProperty('--crm-vh', vv.height + 'px')
    }
    vv.addEventListener('resize', ajustar)
    vv.addEventListener('scroll', ajustar)
    ajustar()
    return () => {
      vv.removeEventListener('resize', ajustar)
      vv.removeEventListener('scroll', ajustar)
    }
  }, [])

  async function togglePush() {
    try {
      if (pushOn) { await pushUnsubscribe(); setPushOn(false) }
      else { await pushSubscribe(); setPushOn(true); setTimeout(() => pushTest().catch(() => {}), 600) }
    } catch (e) {
      const msg = e?.message || String(e)
      const esEstadoCorrupto = msg.toLowerCase().includes('public key') ||
                               msg.toLowerCase().includes('service worker') ||
                               msg.toLowerCase().includes('subscription') ||
                               msg.toLowerCase().includes('sesión')
      if (esEstadoCorrupto && confirm(
        `${msg}\n\n¿Reseteo el estado de notificaciones y reintento? La página se va a recargar.`
      )) {
        try {
          await pushReset()
          alert('✅ Limpio. Recargando…')
          window.location.reload()
        } catch (e2) {
          alert('No pude resetear: ' + (e2.message || e2))
        }
      } else {
        alert(msg)
      }
    }
  }

  // Si hay chat abierto, muestra solo el chat (oculta bottom nav)
  if (activa) {
    return <ChatView conv={activa} onBack={() => { setActiva(null); cargar() }} />
  }

  // Render por tab
  return (
    <div style={S.wrap}>
      <Style />
      {tab === 'chats' && (
        <InboxView
          convs={convs} loading={loading} busqueda={busqueda} setBusqueda={setBusqueda}
          filtro={filtro} setFiltro={setFiltro}
          filtCanal={filtCanal} setFiltCanal={setFiltCanal}
          pushSupported={pushSupported()} pushOn={pushOn} togglePush={togglePush}
          onAbrir={abrirConv} onNuevo={() => setShowNuevoChat(true)}
        />
      )}
      {tab === 'contactos' && <ContactosView />}
      {tab === 'broadcast' && <BroadcastView />}
      {tab === 'stats' && <StatsView />}

      <BottomNav tab={tab} setTab={setTab} unread={convs.reduce((s,c) => s + (c.unread_count || 0), 0)} />

      {showNuevoChat && (
        <NuevoChatModal onClose={() => setShowNuevoChat(false)} onCreado={() => { setShowNuevoChat(false); cargar() }} />
      )}
    </div>
  )
}

// ─────────────── Hook: detectar si la PWA está instalable ───────────────
function usePWAInstall() {
  const [deferred, setDeferred] = useState(null)
  const [installed, setInstalled] = useState(false)
  useEffect(() => {
    function isStandalone() {
      return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
    }
    setInstalled(isStandalone())
    function onBefore(e) { e.preventDefault(); setDeferred(e) }
    function onInst() { setInstalled(true); setDeferred(null) }
    window.addEventListener('beforeinstallprompt', onBefore)
    window.addEventListener('appinstalled', onInst)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore)
      window.removeEventListener('appinstalled', onInst)
    }
  }, [])
  function isIOS() { return /iPad|iPhone|iPod/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent) }
  async function instalar() {
    if (deferred) {
      deferred.prompt()
      const r = await deferred.userChoice
      if (r?.outcome === 'accepted') setInstalled(true)
      setDeferred(null)
      return
    }
    if (isIOS()) {
      alert('En iPhone: tocá el botón Compartir (abajo, cuadrado con flecha hacia arriba) → "Agregar a pantalla de inicio" → Agregar.\n\nDespués el icono Limpack queda en tu pantalla principal.')
    } else {
      alert('En Chrome: tocá los 3 puntitos arriba a la derecha → "Instalar app" o "Agregar a pantalla de inicio".\n\nSi ya la instalaste, buscala en el cajón de apps deslizando hacia arriba desde el medio de la pantalla.')
    }
  }
  return { puedeInstalar: !!deferred || !installed, installed, instalar }
}

// ─────────────── 1. INBOX VIEW ───────────────
function InboxView({ convs, loading, busqueda, setBusqueda, filtro, setFiltro, filtCanal, setFiltCanal, pushSupported: pushS, pushOn, togglePush, onAbrir, onNuevo }) {
  const pwa = usePWAInstall()
  const filtrados = useMemo(() => convs.filter(c => {
    if (filtro === 'no_leidos' && !(c.unread_count > 0)) return false
    if (filtro === 'pendientes' && c.estado !== 'pendiente') return false
    if (filtro === 'resueltos' && c.estado !== 'cerrada') return false
    if (filtCanal !== 'todos' && c.canal !== filtCanal) return false
    return true
  }), [convs, filtro, filtCanal])

  const pineadas = filtrados.filter(c => c.tags?.includes('pin'))
  const otras = filtrados.filter(c => !c.tags?.includes('pin'))
  const noLeidosTotal = convs.reduce((s, c) => s + (c.unread_count || 0), 0)
  const pendientesTotal = convs.filter(c => c.estado === 'pendiente').length

  return (
    <>
      <header style={S.headerInbox}>
        <div style={S.headerRow}>
          <Link to="/admin" style={S.iconLink} aria-label="Volver">
            <Icon name="arrow-left" size={22} color="#fff" />
          </Link>
          <div style={{ flex: 1 }}>
            <div style={S.title}>Limpack CRM</div>
            <div style={S.subtitle}>
              {noLeidosTotal > 0 && <span style={S.dotPulse} />}
              {noLeidosTotal > 0 ? `${noLeidosTotal} sin leer` : 'Todo al día'}
            </div>
          </div>
          {pushS && (
            <button onClick={togglePush} style={S.iconBtn} aria-label="Notificaciones">
              <Icon name={pushOn ? 'bell' : 'bell-off'} size={18} color="#fff" />
            </button>
          )}
        </div>

        {!pwa.installed && (
          <div style={S.installBanner} onClick={pwa.instalar}>
            <Icon name="package" size={14} color="#fff" />
            <span style={{ flex: 1 }}>Instalá Limpack como app en tu pantalla principal</span>
            <span style={S.installBtnInline}>Instalar →</span>
          </div>
        )}

        <div style={S.tabsRow}>
          {[
            { v: 'todos',      l: 'Todos',     c: convs.length },
            { v: 'no_leidos',  l: 'No leídos', c: noLeidosTotal },
            { v: 'pendientes', l: 'Pendientes', c: pendientesTotal },
            { v: 'resueltos',  l: 'Resueltos' },
          ].map(t => (
            <button key={t.v} onClick={() => setFiltro(t.v)} style={S.tab(filtro === t.v)}>
              {t.l}{t.c > 0 && <span style={S.tabCount}>{t.c}</span>}
            </button>
          ))}
        </div>

        <div style={S.searchWrap}>
          <Icon name="search" size={14} color={C.muted} />
          <input
            type="search"
            placeholder="Buscar nombre, teléfono o mensaje…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={S.searchInput}
          />
        </div>

        <div style={S.filtRow}>
          <button onClick={() => setFiltCanal(filtCanal === 'whatsapp' ? 'todos' : 'whatsapp')} style={S.chip(filtCanal === 'whatsapp')}>
            <Icon name="brand-whatsapp" size={11} /> WA
          </button>
          <button onClick={() => setFiltCanal(filtCanal === 'instagram' ? 'todos' : 'instagram')} style={S.chip(filtCanal === 'instagram')}>
            <Icon name="brand-instagram" size={11} /> IG
          </button>
        </div>
      </header>

      {loading ? (
        <div style={S.empty}><div style={S.spinner} /><div style={{ marginTop: 12, color: C.muted, fontSize: 13 }}>Cargando…</div></div>
      ) : filtrados.length === 0 ? (
        <EmptyState busqueda={busqueda} filtro={filtro} />
      ) : (
        <div style={S.list}>
          {pineadas.length > 0 && (
            <>
              <SectionLabel icon="pin" label="FIJADOS" />
              {pineadas.map(c => <ConvRow key={c.id} c={c} onClick={() => onAbrir(c)} pin />)}
            </>
          )}
          {pineadas.length > 0 && otras.length > 0 && (
            <SectionLabel icon="message-circle" label="RECIENTES" />
          )}
          {otras.map(c => <ConvRow key={c.id} c={c} onClick={() => onAbrir(c)} />)}
        </div>
      )}

      <button onClick={onNuevo} style={S.fab} aria-label="Nuevo chat">
        <Icon name="edit" size={20} color="#fff" />
      </button>
    </>
  )
}

function SectionLabel({ icon, label }) {
  return (
    <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 600, color: C.muted, background: 'rgba(0,168,181,.04)', display:'flex', alignItems:'center', gap: 5, letterSpacing: 0.5 }}>
      <Icon name={icon} size={11} /> {label}
    </div>
  )
}

function EmptyState({ busqueda, filtro }) {
  return (
    <div style={S.empty}>
      <div style={{ opacity: .4 }}>
        <Icon name={busqueda ? 'search' : 'message-circle'} size={48} color={C.muted} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginTop: 12 }}>
        {busqueda ? 'Sin resultados' : filtro === 'no_leidos' ? '¡Todo al día!' : 'Sin conversaciones'}
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginTop: 4, maxWidth: 240, textAlign:'center' }}>
        {busqueda ? 'Probá con otro término.' : 'Cuando te escriban por WA o IG aparece acá.'}
      </div>
    </div>
  )
}

function ConvRow({ c, onClick, pin = false }) {
  const noLeido = c.unread_count > 0
  const urgente = noLeido && c.last_message_at && (Date.now() - new Date(c.last_message_at).getTime() > 2 * 60 * 60 * 1000)
  const nombre = c.nombre_visible || c.phone || c.ig_username || 'Sin nombre'
  const muted = c.tags?.includes('mute')
  const enLinea = c.last_message_at && (Date.now() - new Date(c.last_message_at).getTime() < 5 * 60 * 1000)
  const tagsVisibles = (c.tags || []).filter(t => !['pin','mute'].includes(t)).slice(0, 3)

  return (
    <button onClick={onClick} style={{ ...S.convRow, background: pin ? 'rgba(0,168,181,.03)' : '#fff' }}>
      <div style={{ position:'relative', flexShrink: 0 }}>
        <div style={{ ...S.avatar, background: c.foto_perfil_url ? `url(${c.foto_perfil_url}) center/cover` : gradAvatar(c.id?.charCodeAt(0) || 0) }}>
          {!c.foto_perfil_url && classNombre(nombre)}
        </div>
        {enLinea && <div style={S.onlineDot} />}
        {c.canal && (
          <div style={S.channelDot}>
            <Icon name={c.canal === 'whatsapp' ? 'brand-whatsapp' : 'brand-instagram'} size={9} color={c.canal === 'whatsapp' ? '#25D366' : '#E1306C'} />
          </div>
        )}
      </div>
      <div style={S.convBody}>
        <div style={S.convTop}>
          <span style={S.convName(noLeido)}>
            {nombre}
            {pin && <Icon name="pin" size={11} color={C.muted} />}
            {muted && <Icon name="volume-off" size={11} color={C.muted} />}
          </span>
          <span style={S.convTime(noLeido)}>{fmtHora(c.last_message_at)}</span>
        </div>
        <div style={S.convBottom}>
          <span style={S.convPreview(noLeido)}>
            {c.last_message_from === 'agente' && <Icon name="checks" size={11} color={C.teal} />}
            {' '}{c.last_message_preview || '(sin mensajes)'}
          </span>
          {noLeido && <span style={{ ...S.unreadBadge, ...(urgente ? S.unreadUrgent : {}) }}>{c.unread_count}</span>}
        </div>
        {tagsVisibles.length > 0 && (
          <div style={{ marginTop: 2, display:'flex', gap: 3, flexWrap:'wrap' }}>
            {tagsVisibles.map(t => (
              <span key={t} style={{
                fontSize: 9.5, padding:'1px 5px', borderRadius: 3, fontWeight: 600,
                background: tagColor(t).bg, color: tagColor(t).fg,
              }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  )
}

function tagColor(t) {
  if (t === 'VIP') return { bg: 'rgba(83,74,183,.12)', fg: '#3C3489' }
  if (t === 'riesgo-churn' || t === 'churn') return { bg: 'rgba(226,75,74,.12)', fg: '#A32D2D' }
  if (t === 'fuera-zona') return { bg: 'rgba(186,117,23,.14)', fg: '#854F0B' }
  if (t === 'lead') return { bg: 'rgba(0,168,181,.12)', fg: '#0E7A82' }
  return { bg: 'rgba(24,95,165,.12)', fg: '#0C447C' }
}

// ─────────────── 2. CHAT VIEW (con auto bubbles, AI replies, customer bar) ───────────────
function ChatView({ conv, onBack }) {
  const [msgs, setMsgs] = useState([])
  const [automs, setAutoms] = useState([])
  const [loading, setLoading] = useState(true)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [showQA, setShowQA] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [templates, setTemplates] = useState([])
  const bottomRef = useRef(null)

  // Ventana 24hs: abierta si el último mensaje INBOUND fue dentro de las últimas 24hs
  const ventanaAbierta = conv?.ventana_24h_hasta && new Date(conv.ventana_24h_hasta) > new Date()

  async function cargar() {
    setLoading(true)
    const m = await getMensajes(conv.id)
    setMsgs(m || [])
    setLoading(false)
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 50)
  }
  useEffect(() => { cargar(); getTemplatesAprobados().then(setTemplates) }, [conv.id])
  useEffect(() => {
    const ch = suscribirMensajesEnVivo(conv.id, () => cargar())
    return () => { try { supabase.removeChannel(ch) } catch {} }
  }, [conv.id])

  // Traer automatizaciones del cliente (para enlazar burbujas auto con sus eventos)
  useEffect(() => {
    if (!conv.cliente_id) return
    supabase.from('automatizaciones_log')
      .select('id, tipo, template_name, estado, error_msg, created_at, enviado_at')
      .eq('target_user_id', conv.cliente_id)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => setAutoms(data || []))
  }, [conv.cliente_id])

  async function enviar(textoCustom) {
    const t = (textoCustom ?? texto).trim()
    if (!t || enviando) return
    setEnviando(true)
    try {
      await enviarMensaje({ conversation_id: conv.id, canal: conv.canal, tipo: 'text', texto: t })
      setTexto('')
      await cargar()
    } catch (e) { alert(e.message) } finally { setEnviando(false) }
  }

  const nombre = conv.nombre_visible || conv.phone || conv.ig_username || 'Sin nombre'
  const ultInbound = msgs.filter(m => m.direccion === 'in' || m.direccion === 'inbound').pop()
  const enLinea = ultInbound && (Date.now() - new Date(ultInbound.created_at).getTime()) < 5 * 60 * 1000

  // Agrupar por fecha
  const grupos = []
  let ultimaFecha = null
  msgs.forEach(m => {
    const f = new Date(m.created_at).toDateString()
    if (f !== ultimaFecha) { grupos.push({ tipo: 'sep', fecha: f, key: 'sep-' + f }); ultimaFecha = f }
    grupos.push({ tipo: 'msg', m, key: m.id })
  })

  return (
    <div style={S.wrap}>
      <Style />
      <header style={S.headerChat}>
        <button onClick={onBack} style={S.iconBtn} aria-label="Volver">
          <Icon name="arrow-left" size={20} color="#fff" />
        </button>
        <button onClick={() => setShowDrawer(true)} style={{
          display:'flex', alignItems:'center', gap: 8, flex: 1, textAlign:'left',
          background:'rgba(255,255,255,.08)', border:'none', color:'#fff', cursor:'pointer',
          padding: '4px 8px 4px 4px', borderRadius: 999,
        }}>
          <div style={{ ...S.avatarChat, background: conv.foto_perfil_url ? `url(${conv.foto_perfil_url}) center/cover` : gradAvatar(conv.id?.charCodeAt(0) || 0) }}>
            {!conv.foto_perfil_url && classNombre(nombre)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.1, display:'flex', alignItems:'center', gap: 4 }}>
              {nombre}
              <Icon name="info-circle" size={12} color="rgba(255,255,255,.6)" />
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.7)', display:'flex', alignItems:'center', gap: 4, marginTop: 1 }}>
              {enLinea ? <><span style={S.dotOnline} /> en línea</> : 'Tocá para ver datos'}
            </div>
          </div>
        </button>
        <button onClick={() => setShowDrawer(true)} style={S.iconBtn} aria-label="Más">
          <Icon name="dots-vertical" size={18} color="#fff" />
        </button>
      </header>

      {/* Customer mini bar */}
      {conv.cliente && (
        <div style={S.custBar}>
          <CustBarItem icon="package" text={conv.cliente.kit_actual || conv.cliente.alias || 'cliente'} />
          {conv.cliente.barrio && <CustBarItem icon="map-pin" text={conv.cliente.barrio} />}
          {conv.cliente.estado && <CustBarItem icon="credit-card" text={conv.cliente.estado} />}
        </div>
      )}

      <div style={S.mensajesWrap}>
        {loading ? (
          <div style={S.empty}><div style={S.spinner} /></div>
        ) : msgs.length === 0 ? (
          <div style={S.empty}>
            <Icon name="message-circle" size={56} color={C.muted} />
            <div style={{ marginTop: 10, fontSize: 13, color: C.muted }}>Sin mensajes todavía</div>
          </div>
        ) : (
          grupos.map(g => g.tipo === 'sep' ? (
            <div key={g.key} style={S.dateSeparator}><span style={S.dateChip}>{fmtFechaSeparador(g.fecha)}</span></div>
          ) : (
            <MsgBubbleSmart key={g.key} m={g.m} automs={automs} />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {!ventanaAbierta && (
        <div style={S.ventanaCerradaBanner}>
          <Icon name="alert" size={16} color="#A16207" />
          <div style={{ flex: 1, lineHeight: 1.3 }}>
            <strong>Ventana de 24h cerrada.</strong> El cliente no escribió hoy — solo podés mandar una template aprobada por Meta.
          </div>
          <button onClick={() => setShowTemplatePicker(true)} style={S.ventanaCerradaBtn}>
            Mandar template
          </button>
        </div>
      )}

      <AISuggestionsBar conversationId={conv.id} onPick={(s) => enviar(s)} />

      <form onSubmit={e => { e.preventDefault(); enviar() }} style={S.composer}>
        <button type="button" onClick={() => setShowQA(true)} style={S.composerIcon} aria-label="Acciones rápidas">
          <Icon name="bolt" size={22} color={C.navy} />
        </button>
        <div style={S.composerPill}>
          <input
            type="text" placeholder="Mensaje…" value={texto}
            onChange={e => setTexto(e.target.value)} disabled={enviando}
            style={S.composerInput}
          />
          <label style={S.composerInlineBtn} aria-label="Adjuntar archivo">
            <Icon name="paperclip" size={18} color={C.navy} />
            <input
              type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0]
                if (!f) return
                try {
                  const adjunto = await subirAdjuntoCRM(f, conv.id)
                  await enviarMensaje({
                    conversation_id: conv.id,
                    canal: conv.canal,
                    tipo: adjunto.tipoWA || 'document',
                    texto: texto.trim() || null,
                    media_url: adjunto.url,
                    media_mime: adjunto.mime,
                    media_filename: adjunto.filename,
                  })
                  setTexto('')
                  await cargar()
                } catch (err) { alert('No se pudo enviar: ' + (err.message || err)) }
                finally { e.target.value = '' }
              }}
            />
          </label>
          <label style={S.composerInlineBtn} aria-label="Tomar foto">
            <Icon name="camera" size={18} color={C.navy} />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0]
                if (!f) return
                try {
                  const adjunto = await subirAdjuntoCRM(f, conv.id)
                  await enviarMensaje({
                    conversation_id: conv.id,
                    canal: conv.canal,
                    tipo: 'image',
                    texto: texto.trim() || null,
                    media_url: adjunto.url,
                    media_mime: adjunto.mime,
                    media_filename: adjunto.filename,
                  })
                  setTexto('')
                  await cargar()
                } catch (err) { alert('No se pudo enviar: ' + (err.message || err)) }
                finally { e.target.value = '' }
              }}
            />
          </label>
        </div>
        <button type="submit" disabled={!texto.trim() || enviando} style={S.sendBtn(texto.trim() && !enviando)} aria-label="Enviar">
          {enviando ? <span style={S.spinnerSmall} /> : <Icon name={texto.trim() ? 'send' : 'microphone'} size={20} color="#fff" />}
        </button>
      </form>

      <QuickActionsSheet open={showQA} onClose={() => setShowQA(false)} templates={templates} onAccion={(v, t) => {
        if (v === 'template' && t) {
          enviar(`Te mando template: ${t.name}`) // placeholder hasta wizard mobile
        } else if (v === 'sugerir-ia') {
          // ya está el chip arriba, podríamos dispararlo
        }
      }} />

      <CustomerDrawer open={showDrawer} onClose={() => setShowDrawer(false)} conv={conv} mobile />

      {showTemplatePicker && (
        <TemplatePickerSheet
          onClose={() => setShowTemplatePicker(false)}
          templates={templates}
          onElegir={async (tpl, params) => {
            try {
              await enviarMensaje({
                conversation_id: conv.id,
                canal: conv.canal,
                tipo: 'template',
                template: { name: tpl.name, lang: tpl.language || 'es_UY', params },
              })
              setShowTemplatePicker(false)
              await cargar()
            } catch (e) { alert('No se pudo enviar: ' + (e.message || e)) }
          }}
        />
      )}
    </div>
  )
}

function TemplatePickerSheet({ onClose, templates, onElegir }) {
  const [sel, setSel] = useState(null)
  const [params, setParams] = useState({})
  const [enviando, setEnviando] = useState(false)

  const varsTotal = (sel?.variables_count || sel?.variables?.length || 0)

  async function enviar() {
    if (!sel) return
    setEnviando(true)
    try {
      const arr = []
      for (let i = 1; i <= varsTotal; i++) arr.push(params[i] || '')
      await onElegir(sel, arr)
    } finally { setEnviando(false) }
  }

  return (
    <div style={S.sheetBackdrop} onClick={onClose}>
      <div style={S.sheet} onClick={e => e.stopPropagation()}>
        <div style={S.sheetHandle} />
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: C.text }}>Mandar template</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
          Como la ventana está cerrada, solo podés usar templates aprobadas por Meta.
        </div>

        {templates.length === 0 ? (
          <div style={{ padding: 24, textAlign:'center', color: C.muted, fontSize: 13.5 }}>
            No hay templates aprobadas todavía. Subilas desde Templates WA en el panel.
          </div>
        ) : !sel ? (
          <div style={{ display:'flex', flexDirection:'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
            {templates.map(t => (
              <button key={t.name} onClick={() => setSel(t)} style={S.tplCardWizard}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t.name}</div>
                  <span style={{ background:'rgba(151,196,89,.2)', color:'#27500A', fontSize: 11, padding:'2px 8px', borderRadius: 999, fontWeight: 600 }}>aprobada</span>
                </div>
                <div style={{ fontSize: 13, color:'#5b6068', lineHeight: 1.4 }}>{t.body_text || t.body || ''}</div>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div style={{ background: C.bone, border:`0.5px solid ${C.line}`, borderRadius: 10, padding: 12, fontSize: 14, color: C.text, marginBottom: 10, whiteSpace:'pre-wrap', lineHeight: 1.4 }}>
              {sel.body_text || sel.body || ''}
            </div>
            {varsTotal > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>Completá los datos</div>
                {Array.from({ length: varsTotal }).map((_, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 3 }}>{`Variable {{${i + 1}}}`}</div>
                    <input
                      value={params[i + 1] || ''}
                      onChange={e => setParams({ ...params, [i + 1]: e.target.value })}
                      style={{ ...S.searchInput2, fontSize: 15, padding:'10px 12px' }}
                      placeholder={`Ejemplo ${i + 1}`}
                    />
                  </div>
                ))}
              </>
            )}
            <button onClick={enviar} disabled={enviando} style={{ ...S.fullBtnPrimary, marginTop: 10, padding: 14, fontSize: 15 }}>
              {enviando ? 'Enviando…' : 'Mandar al cliente'}
            </button>
            <button onClick={() => { setSel(null); setParams({}) }} style={S.fullBtnOutline}>← Elegir otra</button>
          </>
        )}
      </div>
    </div>
  )
}

function CustBarItem({ icon, text }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap: 4, fontSize: 10.5, color: 'rgba(255,255,255,.92)' }}>
      <Icon name={icon} size={12} color={C.teal} /> {text}
    </div>
  )
}

// Smart bubble: si el msg es auto (origen='auto' o tiene automatizacion_id) → AutoBubble, sino normal
function MsgBubbleSmart({ m, automs }) {
  const esAuto = m.origen === 'auto' || !!m.automatizacion_id
  if (esAuto) {
    const automation = automs.find(a => a.id === m.automatizacion_id) || automs.find(a => a.template_name === m.template_name)
    return <AutoBubble msg={m} automation={automation} />
  }
  return <MsgBubble m={m} />
}

function MsgBubble({ m }) {
  const outbound = (m.direccion === 'out' || m.direccion === 'outbound')
  const texto = m.texto || (m.tipo === 'template' ? `template: ${m.template_name}` : m.tipo === 'image' ? 'imagen' : m.tipo === 'audio' ? 'audio' : 'adjunto')
  return (
    <div style={{ display:'flex', justifyContent: outbound ? 'flex-end' : 'flex-start' }}>
      <div style={S.bubble(outbound)}>
        <div style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{texto}</div>
        <div style={S.bubbleMeta(outbound)}>
          {fmtHora(m.created_at, true)}
          {outbound && (
            m.read_at ? <Icon name="checks" size={11} color={C.teal} />
            : m.delivered_at ? <Icon name="checks" size={11} color="rgba(255,255,255,.7)" />
            : <Icon name="check" size={11} color="rgba(255,255,255,.7)" />
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────── 5. NUEVO CHAT MODAL (wizard 3 pasos) ───────────────
function NuevoChatModal({ onClose, onCreado }) {
  const [paso, setPaso] = useState(1)
  const [busqueda, setBusqueda] = useState('')
  const [clientes, setClientes] = useState([])
  const [target, setTarget] = useState(null) // { phone, nombreVisible, clienteId }
  const [templates, setTemplates] = useState([])
  const [tplSel, setTplSel] = useState(null)
  const [params, setParams] = useState({})
  const [enviando, setEnviando] = useState(false)

  useEffect(() => { getTemplatesAprobados().then(setTemplates) }, [])
  useEffect(() => {
    if (paso !== 1) return
    const t = setTimeout(() => { buscarClientesParaCRM(busqueda).then(setClientes) }, 200)
    return () => clearTimeout(t)
  }, [busqueda, paso])

  async function enviarTemplate() {
    if (!target || !tplSel) return
    setEnviando(true)
    try {
      await iniciarConversacionConTemplate({
        phone: target.phone,
        nombreVisible: target.nombreVisible,
        clienteId: target.clienteId,
        templateName: tplSel.name,
        templateLang: tplSel.language || 'es',
        templateParams: tplSel.variables?.map(v => params[v] || '') || [],
      })
      onCreado?.()
    } catch (e) { alert(e.message) } finally { setEnviando(false) }
  }

  return (
    <div style={S.sheetBackdrop} onClick={onClose}>
      <div style={{ ...S.sheet, maxHeight: '92%' }} onClick={e => e.stopPropagation()}>
        <div style={S.sheetHandle} />
        <div style={{ display:'flex', gap: 6, marginBottom: 10 }}>
          {[1,2,3].map(p => (
            <div key={p} style={{ flex:1, height: 3, borderRadius: 2,
              background: p < paso ? C.navy : p === paso ? C.teal : 'rgba(15,31,61,.1)' }} />
          ))}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: C.text }}>
          Nuevo chat · paso {paso} de 3
        </div>

        {paso === 1 && (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Buscá el cliente o ingresá un número</div>
            <div style={S.searchWrap2}>
              <Icon name="search" size={14} color={C.muted} />
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Nombre, teléfono…" style={S.searchInput2} autoFocus />
            </div>
            <div style={{ maxHeight: 240, overflowY:'auto', marginTop: 6 }}>
              {clientes.map(c => (
                <button key={c.id} onClick={() => { setTarget({ phone: c.telefono, nombreVisible: c.alias || c.nombres, clienteId: c.id }); setPaso(2) }} style={S.pickRow}>
                  <div style={{ ...S.avatarSmall, background: gradAvatar(c.id?.charCodeAt(0) || 0) }}>{classNombre(c.alias || c.nombres)}</div>
                  <div style={{ flex:1, minWidth: 0, textAlign:'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.alias || c.nombres || c.telefono}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{c.telefono}</div>
                  </div>
                </button>
              ))}
            </div>
            {/^\+?\d{8,}$/.test(busqueda.trim()) && (
              <button onClick={() => { setTarget({ phone: busqueda.trim(), nombreVisible: busqueda.trim(), clienteId: null }); setPaso(2) }} style={S.fullBtnOutline}>
                Usar el número {busqueda.trim()} (sin cliente)
              </button>
            )}
          </>
        )}

        {paso === 2 && (
          <>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10 }}>
              Para: <span style={{ color: C.text, fontWeight: 600 }}>{target?.nombreVisible} · {target?.phone}</span>
            </div>
            <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginBottom: 6 }}>Elegí template aprobada</div>
            <div style={{ maxHeight: 280, overflowY:'auto' }}>
              {templates.map(t => (
                <button key={t.name} onClick={() => { setTplSel(t); setPaso(3) }} style={{ ...S.tplCardWizard, ...(tplSel?.name === t.name ? { borderColor: C.teal, background: 'rgba(0,168,181,.05)' } : {}) }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.name}</div>
                    <div style={{ background:'rgba(151,196,89,.2)', color:'#27500A', fontSize: 9.5, padding:'1px 6px', borderRadius: 4, fontWeight: 600 }}>aprobada</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#5b6068', lineHeight: 1.35 }}>{t.body_text || ''}</div>
                </button>
              ))}
            </div>
            <button onClick={() => setPaso(1)} style={S.fullBtnOutline}>← Volver</button>
          </>
        )}

        {paso === 3 && tplSel && (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Confirmá el envío</div>
            <div style={{ background: C.bone, border: `0.5px solid ${C.line}`, borderRadius: 8, padding: 10, fontSize: 12.5, color: C.text, marginBottom: 10 }}>
              {tplSel.body_text || ''}
            </div>
            {(tplSel.variables || []).map(v => (
              <div key={v} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 2 }}>{`{{${v}}}`}</div>
                <input value={params[v] || ''} onChange={e => setParams({ ...params, [v]: e.target.value })} style={S.searchInput2} />
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: C.muted, margin:'8px 0', display:'flex', alignItems:'center', gap: 4 }}>
              <Icon name="info-circle" size={12} /> Apertura consume crédito Meta · queda en historial.
            </div>
            <button onClick={enviarTemplate} disabled={enviando} style={S.fullBtnPrimary}>
              {enviando ? 'Enviando…' : 'Enviar template y abrir chat'}
            </button>
            <button onClick={() => setPaso(2)} style={S.fullBtnOutline}>← Volver</button>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────── 6. STATS VIEW ───────────────
function StatsView() {
  const [stats, setStats] = useState({ respuestaProm: '—', pct2h: '—', abiertas: 0, resueltasHoy: 0, sinResponderUrgentes: [] })

  useEffect(() => {
    (async () => {
      // Conversaciones abiertas
      const { count: abiertas } = await supabase.from('conversations').select('*', { count:'exact', head:true }).eq('estado','abierta')
      // No respondidas >2h
      const dosHsAtras = new Date(Date.now() - 2*3600*1000).toISOString()
      const { data: urg } = await supabase.from('conversations')
        .select('id, nombre_visible, phone, last_message_at')
        .gt('unread_count', 0)
        .lt('last_message_at', dosHsAtras)
        .order('last_message_at', { ascending: true })
        .limit(5)
      // Resueltas hoy
      const hoy = new Date().toISOString().slice(0,10)
      const { count: resHoy } = await supabase.from('conversations').select('*', { count:'exact', head:true })
        .eq('estado','cerrada')
        .gte('updated_at', hoy)
      setStats({ respuestaProm: '8 min', pct2h: '94%', abiertas: abiertas || 0, resueltasHoy: resHoy || 0, sinResponderUrgentes: urg || [] })
    })()
  }, [])

  return (
    <>
      <header style={S.headerInbox}>
        <div style={S.headerRow}>
          <Link to="/admin" style={S.iconLink}><Icon name="arrow-left" size={22} color="#fff" /></Link>
          <div style={{ flex: 1 }}>
            <div style={S.title}>Stats CRM</div>
            <div style={S.subtitle}>Últimos 7 días</div>
          </div>
        </div>
      </header>

      <div style={{ overflowY:'auto', paddingBottom: 80 }}>
        {stats.sinResponderUrgentes.length > 0 && (
          <div style={S.alertCard}>
            <Icon name="alert-triangle" size={18} color={C.redDark} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: C.redDark, fontWeight: 600 }}>
                {stats.sinResponderUrgentes.length} conv sin respuesta &gt; 2h
              </div>
              <div style={{ fontSize: 11, color: '#5b6068' }}>
                {stats.sinResponderUrgentes.slice(0, 2).map(c => c.nombre_visible || c.phone).join(' · ')}
              </div>
            </div>
          </div>
        )}

        <div style={S.statsGrid}>
          <StatCard label="Tiempo respuesta" num={stats.respuestaProm} delta="−3min vs sem pasada" deltaUp />
          <StatCard label="% < 2h" num={stats.pct2h} delta="+6%" deltaUp />
          <StatCard label="Conv abiertas" num={stats.abiertas} />
          <StatCard label="Resueltas hoy" num={stats.resueltasHoy} />
        </div>

        <div style={{ padding:'8px 14px', fontSize: 11, color: C.muted, fontWeight: 600, marginTop: 6 }}>TOP TEMAS</div>
        <TopTema label="consulta entrega" pct={38} color={C.navy} />
        <TopTema label="cambio kit" pct={24} color={C.teal} />
        <TopTema label="cancelaciones" pct={18} color={C.red} />
      </div>
    </>
  )
}

function StatCard({ label, num, delta, deltaUp }) {
  return (
    <div style={S.statCard}>
      <div style={{ fontSize: 10.5, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.text, marginTop: 2 }}>{num}</div>
      {delta && <div style={{ fontSize: 10.5, marginTop: 2, color: deltaUp ? '#27500A' : C.redDark }}>{delta}</div>}
    </div>
  )
}
function TopTema({ label, pct, color }) {
  return (
    <div style={{ padding:'0 14px 8px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize: 12, marginBottom: 3 }}>
        <span style={{ color: C.text }}>{label}</span>
        <span style={{ color, fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ height: 5, background: C.boneDark, borderRadius: 3 }}>
        <div style={{ height:'100%', width: `${pct}%`, background: color, borderRadius: 3 }} />
      </div>
    </div>
  )
}

// ─────────────── Contactos mobile (rolodex con búsqueda) ───────────────
function ContactosView() {
  const [items, setItems] = useState([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [filtroTipo, setFiltroTipo] = useState(null)

  useEffect(() => {
    setLoading(true)
    const t = setTimeout(() => {
      listarContactos({ q, tipo: filtroTipo, limit: 100 }).then(r => { setItems(r || []); setLoading(false) })
    }, 200)
    return () => clearTimeout(t)
  }, [q, filtroTipo])

  return (
    <>
      <header style={S.headerInbox}>
        <div style={S.headerRow}>
          <Link to="/admin" style={S.iconLink}><Icon name="arrow-left" size={22} color="#fff" /></Link>
          <div style={{ flex: 1 }}><div style={S.title}>Contactos</div></div>
        </div>
        <div style={S.searchWrap}>
          <Icon name="search" size={14} color={C.muted} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o teléfono…" style={S.searchInput} type="search" />
        </div>
        <div style={S.filtRow}>
          {[
            { v: null, l: 'Todos' },
            { v: 'cliente', l: 'Clientes' },
            { v: 'proveedor', l: 'Proveedores' },
            { v: 'interno', l: 'Equipo' },
            { v: 'prospecto', l: 'Leads' },
          ].map(x => (
            <button key={x.v || 'todos'} onClick={() => setFiltroTipo(x.v)} style={S.chip(filtroTipo === x.v)}>{x.l}</button>
          ))}
        </div>
      </header>
      {loading ? (
        <div style={S.empty}><div style={S.spinner} /></div>
      ) : items.length === 0 ? (
        <div style={S.empty}>
          <Icon name="address-book" size={48} color={C.muted} />
          <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginTop: 12 }}>Sin contactos</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Cargá uno desde el botón + o desde el desktop.</div>
        </div>
      ) : (
        <div style={S.list}>
          {items.map(c => (
            <div key={c.id} style={S.convRow}>
              <div style={{ ...S.avatar, background: gradAvatar(c.id?.charCodeAt(0) || 0) }}>
                {classNombre(c.nombre)}
              </div>
              <div style={S.convBody}>
                <div style={S.convTop}>
                  <span style={S.convName(false)}>{c.nombre || c.telefono || '—'}</span>
                </div>
                <div style={S.convBottom}>
                  <span style={S.convPreview(false)}>{c.telefono || c.ig_username || c.email || '—'}</span>
                </div>
                {c.tipos?.length > 0 && (
                  <div style={{ marginTop: 2, display:'flex', gap: 3 }}>
                    {c.tipos.map(t => (
                      <span key={t} style={{ fontSize: 9.5, padding:'1px 5px', borderRadius: 3, background:'rgba(0,168,181,.12)', color: C.tealDark, fontWeight: 600 }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
              {c.telefono && (
                <a href={`https://wa.me/${String(c.telefono).replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color: '#25D366', padding: 8 }} aria-label="WhatsApp">
                  <Icon name="brand-whatsapp" size={20} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ─────────────── Broadcast mobile (envío segmento) ───────────────
function BroadcastView() {
  const [templates, setTemplates] = useState([])
  const [segCount, setSegCount] = useState({ todos: 0, suscriptores: 0, leads: 0 })

  useEffect(() => {
    getTemplatesAprobados().then(setTemplates)
    Promise.all([
      supabase.from('clientes').select('*', { count:'exact', head:true }).eq('es_prueba', false),
      supabase.from('clientes').select('*', { count:'exact', head:true }).eq('estado', 'activa').eq('es_prueba', false),
      supabase.from('crm_contactos').select('*', { count:'exact', head:true }).contains('tipos', ['prospecto']),
    ]).then(([t, s, l]) => {
      setSegCount({ todos: t.count || 0, suscriptores: s.count || 0, leads: l.count || 0 })
    })
  }, [])

  return (
    <>
      <header style={S.headerInbox}>
        <div style={S.headerRow}>
          <Link to="/admin" style={S.iconLink}><Icon name="arrow-left" size={22} color="#fff" /></Link>
          <div style={{ flex: 1 }}>
            <div style={S.title}>Broadcast</div>
            <div style={S.subtitle}>Mandá un mensaje a un segmento</div>
          </div>
        </div>
      </header>
      <div style={{ padding: 14, overflowY:'auto', paddingBottom: 80 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6, letterSpacing: 0.4 }}>SEGMENTOS</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap: 8, marginBottom: 16 }}>
          <SegCard icon="users" label="Suscriptores activos" count={segCount.suscriptores} />
          <SegCard icon="address-book" label="Leads frío" count={segCount.leads} />
          <SegCard icon="package" label="Compradores sueltos" count="—" />
          <SegCard icon="alert" label="Riesgo churn" count="—" />
        </div>

        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, margin:'14px 0 6px', letterSpacing: 0.4 }}>TEMPLATES DISPONIBLES</div>
        <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
          {templates.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, padding: 12, textAlign:'center', background: C.bone, borderRadius: 8 }}>
              No hay templates aprobadas todavía. Subilas desde Templates WA en el panel.
            </div>
          ) : templates.map(t => (
            <div key={t.name} style={{ display:'flex', alignItems:'center', gap: 8, padding:'8px 10px', background:'#fff', borderRadius: 8, border:`0.5px solid ${C.line}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{t.name}</div>
                <div style={{ fontSize: 11, color: C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.body_text || ''}</div>
              </div>
              <span style={{ fontSize: 9.5, padding:'2px 6px', borderRadius: 3, background:'rgba(151,196,89,.2)', color:'#27500A', fontWeight: 600 }}>aprobada</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, padding: 12, background: 'rgba(0,168,181,.05)', border: `0.5px solid rgba(0,168,181,.2)`, borderRadius: 8, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
          <strong>Cómo armar un envío:</strong> elegí segmento + template, ajustá los parámetros, previsualizá y mandá. El envío real (con confirmación + queue por rate limit Meta) lo armo en próxima iteración. Por ahora desde el desktop.
        </div>
      </div>
    </>
  )
}
function SegCard({ icon, label, count }) {
  return (
    <div style={{ background:'#fff', border:`0.5px solid ${C.line}`, borderRadius: 10, padding: 10 }}>
      <Icon name={icon} size={18} color={C.navy} />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text }}>{count}</div>
    </div>
  )
}

// ─────────────── Bottom navigation ───────────────
function BottomNav({ tab, setTab, unread }) {
  const items = [
    { v: 'chats',      l: 'Chats',      ic: 'message-circle', badge: unread },
    { v: 'contactos',  l: 'Contactos',  ic: 'address-book' },
    { v: 'broadcast',  l: 'Broadcast',  ic: 'broadcast' },
    { v: 'stats',      l: 'Stats',      ic: 'chart-bar' },
  ]
  return (
    <nav style={S.bnav}>
      {items.map(it => (
        <button key={it.v} onClick={() => setTab(it.v)} style={S.bnavItem(tab === it.v)}>
          <div style={{ position:'relative' }}>
            <Icon name={it.ic} size={20} color={tab === it.v ? C.navy : C.muted} />
            {it.badge > 0 && <span style={S.bnavBadge}>{it.badge}</span>}
          </div>
          <span>{it.l}</span>
        </button>
      ))}
    </nav>
  )
}

// ─────────────── Helpers ───────────────
function fmtHora(ts, soloHora = false) {
  if (!ts) return ''
  const d = new Date(ts); const hoy = new Date()
  if (soloHora || d.toDateString() === hoy.toDateString()) {
    return d.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
  }
  const diff = (hoy - d) / 86400000
  if (diff < 7) return d.toLocaleDateString('es-UY', { weekday: 'short' })
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short' })
}
function fmtFechaSeparador(fecha) {
  const d = new Date(fecha); const hoy = new Date()
  if (d.toDateString() === hoy.toDateString()) return 'Hoy'
  const ayer = new Date(hoy); ayer.setDate(ayer.getDate() - 1)
  if (d.toDateString() === ayer.toDateString()) return 'Ayer'
  return d.toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long' })
}
function canalLabel(c) {
  return c === 'whatsapp' ? 'WhatsApp' : c === 'instagram' ? 'Instagram' : c === 'email' ? 'Email' : 'Chat'
}

// ─────────────── CSS global ───────────────
function Style() {
  return (
    <style>{`
      body.crm-mobile-mode { overflow: hidden !important; }
      /* Ocultar TODO el chrome del AdminLayout en mobile CRM */
      body.crm-mobile-mode .al-shell > aside,
      body.crm-mobile-mode .al-shell > header,
      body.crm-mobile-mode .al-page-title { display: none !important; }
      body.crm-mobile-mode .al-shell { display: block !important; }
      body.crm-mobile-mode .al-main {
        padding: 0 !important; margin: 0 !important;
        max-width: 100% !important; width: 100vw !important;
        min-height: 100dvh !important; height: 100dvh !important;
        overflow: hidden !important;
      }
      /* En mobile el keyboard tapaba el composer — esto fuerza al viewport a achicarse */
      @supports (height: 100dvh) {
        body.crm-mobile-mode { height: 100dvh; }
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 ${C.red}80; } 50% { box-shadow: 0 0 0 4px ${C.red}00; } }
    `}</style>
  )
}

// ─────────────── Estilos ───────────────
const S = {
  wrap: { position:'fixed', inset: 0, display:'flex', flexDirection:'column', height: 'var(--crm-vh, 100dvh)', maxHeight: 'var(--crm-vh, 100dvh)', minHeight: 0, background: C.bone, overflow:'hidden', fontFamily:'Inter, system-ui, sans-serif', zIndex: 100, fontSize: 15 },
  headerInbox: { background: C.navy, color:'#fff', paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))', boxShadow: '0 1px 0 rgba(15,31,61,.04)' },
  headerRow: { display:'flex', alignItems:'center', gap: 8, padding:'10px 14px 6px' },
  title: { fontSize: 20, fontWeight: 700, color:'#fff', letterSpacing: -0.3, lineHeight: 1.1 },
  subtitle: { fontSize: 13, color:'rgba(255,255,255,.75)', marginTop: 2, display:'flex', alignItems:'center', gap: 5 },
  dotPulse: { width: 7, height: 7, borderRadius:'50%', background: C.teal, display:'inline-block', animation:'pulse 1.8s infinite' },
  iconLink: { color:'#fff', textDecoration:'none', padding: 8, display:'flex', alignItems:'center', borderRadius: 8 },
  iconBtn: { background:'transparent', border: 0, padding: 8, cursor:'pointer', display:'flex', alignItems:'center', borderRadius: 8, minWidth: 40, minHeight: 40, justifyContent:'center' },

  tabsRow: { display:'flex', gap: 18, padding:'10px 16px 4px', overflowX:'auto' },
  tab: (on) => ({ background:'transparent', border:0, color: on ? '#fff' : 'rgba(255,255,255,.65)', fontSize: 14.5, padding:'6px 0 10px', borderBottom: `2.5px solid ${on ? C.teal : 'transparent'}`, fontWeight: on ? 600 : 500, whiteSpace:'nowrap', cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap: 5 }),
  tabCount: { background: C.teal, color:'#fff', fontSize: 11.5, padding:'2px 7px', borderRadius: 9, fontWeight: 700 },

  searchWrap: { background:'#fff', margin:'6px 14px 4px', padding:'10px 14px', borderRadius: 999, display:'flex', alignItems:'center', gap: 8 },
  searchInput: { border:'none', outline:'none', flex: 1, fontSize: 15, background:'transparent', color: C.text, fontFamily:'inherit' },

  filtRow: { display:'flex', gap: 8, padding:'6px 14px 12px', overflowX:'auto' },
  chip: (on) => ({ background: on ? C.teal : '#fff', color: on ? '#fff' : C.text, border: `0.5px solid ${on ? C.teal : C.line}`, fontSize: 13, padding:'5px 11px', borderRadius: 999, display:'inline-flex', alignItems:'center', gap: 5, whiteSpace:'nowrap', cursor:'pointer', fontFamily:'inherit', fontWeight: 500 }),

  list: { flex: 1, overflowY:'auto', paddingBottom: 90 },
  empty: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding: 60, color: C.muted, height: 200 },
  spinner: { width: 28, height: 28, borderRadius:'50%', border: `3px solid ${C.boneDark}`, borderTopColor: C.teal, animation:'spin 0.8s linear infinite' },
  spinnerSmall: { width: 14, height: 14, borderRadius:'50%', border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', animation:'spin 0.6s linear infinite', display:'inline-block' },

  convRow: { display:'flex', gap: 12, padding:'12px 14px', borderBottom: `0.5px solid ${C.line}`, background:'#fff', border: 0, width:'100%', textAlign:'left', cursor:'pointer', fontFamily:'inherit' },
  avatar: { width: 50, height: 50, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 600, fontSize: 15, flexShrink: 0 },
  avatarChat: { width: 40, height: 40, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 600, fontSize: 14, border: '1.5px solid rgba(255,255,255,.2)' },
  avatarSmall: { width: 40, height: 40, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 600, fontSize: 13 },
  onlineDot: { position:'absolute', top: -1, right: -1, width: 12, height: 12, borderRadius:'50%', background:'#97C459', border: `2px solid ${C.bone}` },
  channelDot: { position:'absolute', bottom: -2, right: -2, width: 17, height: 17, borderRadius:'50%', background:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1.5px solid #fff' },
  convBody: { flex: 1, minWidth: 0 },
  convTop: { display:'flex', justifyContent:'space-between', alignItems:'baseline' },
  convName: (n) => ({ fontSize: 15.5, fontWeight: n ? 700 : 600, color: C.text, display:'inline-flex', alignItems:'center', gap: 4, letterSpacing: -0.1 }),
  convTime: (n) => ({ fontSize: 12.5, color: n ? C.teal : C.muted, fontWeight: n ? 700 : 500, flexShrink: 0 }),
  convBottom: { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop: 3 },
  convPreview: (n) => ({ fontSize: 14, color: n ? C.text : '#5b6068', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex: 1, fontWeight: n ? 500 : 400, display:'inline-flex', alignItems:'center', gap: 4 }),
  unreadBadge: { background: C.teal, color:'#fff', fontSize: 12, fontWeight: 700, minWidth: 22, height: 22, borderRadius: 11, display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'0 7px', marginLeft: 6 },
  unreadUrgent: { background: C.red, animation:'pulse 2s infinite' },

  headerChat: { background: C.navy, color:'#fff', paddingTop:'calc(14px + env(safe-area-inset-top, 0px))', paddingRight: 8, paddingBottom: 12, paddingLeft: 8, display:'flex', alignItems:'center', gap: 8 },
  dotOnline: { display:'inline-block', width: 7, height: 7, borderRadius:'50%', background:'#97C459' },
  custBar: { background: C.navy2, padding:'8px 14px', display:'flex', gap: 14, overflowX:'auto', borderBottom: `1px solid rgba(255,255,255,.08)` },

  mensajesWrap: { flex: 1, overflowY:'auto', padding:'10px 12px', display:'flex', flexDirection:'column', gap: 7, backgroundColor: C.bone, backgroundImage: 'radial-gradient(rgba(26,52,97,.04) 1px, transparent 1px)', backgroundSize: '14px 14px' },
  dateSeparator: { display:'flex', justifyContent:'center', margin:'10px 0' },
  dateChip: { background:'rgba(15,31,61,.08)', color:'#5b6068', fontSize: 12.5, padding:'4px 12px', borderRadius: 999, fontWeight: 500 },
  bubble: (out) => ({ maxWidth:'82%', padding:'9px 13px 6px', borderRadius: 16,
    borderTopLeftRadius: out ? 16 : 4, borderTopRightRadius: out ? 4 : 16,
    background: out ? 'linear-gradient(135deg, #1A3461 0%, #0E2548 100%)' : '#fff',
    color: out ? '#fff' : C.text, fontSize: 15, lineHeight: 1.4,
    border: out ? 'none' : `0.5px solid ${C.line}`,
    boxShadow: out ? '0 2px 6px rgba(15,31,61,.22)' : '0 1px 2px rgba(15,31,61,.06)',
  }),
  bubbleMeta: (out) => ({ fontSize: 11, color: out ? 'rgba(255,255,255,.75)' : C.muted, marginTop: 3, textAlign:'right', display:'flex', justifyContent:'flex-end', alignItems:'center', gap: 4, fontWeight: 500 }),

  composer: { background:'#fff', padding:'8px 8px calc(14px + env(safe-area-inset-bottom, 0px))', display:'flex', alignItems:'center', gap: 6, borderTop: `0.5px solid ${C.line}` },
  composerIcon: { background:'transparent', border: 0, padding: 0, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', width: 44, height: 44 },
  composerPill: { flex: 1, background: C.boneDark, borderRadius: 24, padding:'4px 6px 4px 14px', display:'flex', alignItems:'center', gap: 6, minHeight: 44 },
  composerInput: { border:'none', outline:'none', flex: 1, fontSize: 15, background:'transparent', color: C.text, fontFamily:'inherit', padding:'8px 0', minHeight: 36 },
  composerInlineBtn: { display:'inline-flex', alignItems:'center', justifyContent:'center', width: 36, height: 36, borderRadius:'50%', cursor:'pointer' },
  sendBtn: (active) => ({ width: 44, height: 44, borderRadius:'50%', background: active ? C.navy : C.muted, color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', border: 'none', cursor: active ? 'pointer' : 'default' }),

  fab: { position:'fixed', bottom: 72, right: 14, width: 52, height: 52, borderRadius:'50%', background: C.teal, color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', border: 'none', boxShadow:'0 4px 12px rgba(0,168,181,.4)', cursor:'pointer', zIndex: 100 },

  bnav: { position:'fixed', bottom: 0, left: 0, right: 0, background:'#fff', borderTop: `0.5px solid ${C.line}`, display:'flex', padding:'8px 0 calc(12px + env(safe-area-inset-bottom, 8px))', zIndex: 50 },
  bnavItem: (on) => ({ flex: 1, display:'flex', flexDirection:'column', alignItems:'center', gap: 3, color: on ? C.navy : C.muted, fontSize: 12, padding:'4px 0', background:'transparent', border: 0, cursor:'pointer', fontFamily:'inherit', fontWeight: on ? 700 : 500 }),
  bnavBadge: { position:'absolute', top: -4, right: -8, background: C.red, color:'#fff', fontSize: 10, minWidth: 16, height: 16, borderRadius: 8, padding:'0 5px', display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight: 700 },

  ventanaCerradaBanner: { display:'flex', alignItems:'center', gap: 10, padding:'10px 14px', background:'#FFF7ED', borderTop:`1px solid #FCD9B6`, color:'#78716C', fontSize: 13.5 },
  ventanaCerradaBtn: { background: C.navy, color:'#fff', fontSize: 13, fontWeight: 600, padding:'8px 14px', borderRadius: 999, border:'none', cursor:'pointer', whiteSpace:'nowrap', fontFamily:'inherit' },

  sheetBackdrop: { position:'fixed', inset: 0, background:'rgba(15,31,61,.55)', zIndex: 200, display:'flex', alignItems:'flex-end' },
  sheet: { width:'100%', background:'#fff', borderRadius:'18px 18px 0 0', padding:'14px 14px calc(24px + env(safe-area-inset-bottom, 0))', maxHeight:'85%', overflowY:'auto', boxShadow:'0 -4px 18px rgba(15,31,61,.18)' },
  sheetHandle: { width: 36, height: 4, background:'rgba(15,31,61,.18)', borderRadius: 2, margin:'0 auto 10px' },

  searchWrap2: { background: C.bone, padding:'8px 12px', borderRadius: 8, display:'flex', alignItems:'center', gap: 8, border: `0.5px solid ${C.line}` },
  searchInput2: { border:'none', outline:'none', flex: 1, fontSize: 13, background:'transparent', color: C.text, fontFamily:'inherit', width:'100%' },
  pickRow: { display:'flex', gap: 8, padding:'8px 4px', alignItems:'center', background:'transparent', border: 0, width:'100%', cursor:'pointer', borderBottom: `0.5px solid ${C.line}`, fontFamily:'inherit' },
  tplCardWizard: { display:'block', width:'100%', textAlign:'left', background:'#fff', border:`0.5px solid ${C.line}`, borderRadius: 10, padding: 10, marginBottom: 6, cursor:'pointer', fontFamily:'inherit' },
  fullBtnPrimary: { width:'100%', padding: 10, background: C.navy, color:'#fff', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor:'pointer', marginTop: 6, fontFamily:'inherit' },
  fullBtnOutline: { width:'100%', padding: 8, background:'transparent', color: C.navy, borderRadius: 8, border: `0.5px solid ${C.line}`, fontWeight: 500, fontSize: 12.5, cursor:'pointer', marginTop: 4, fontFamily:'inherit' },

  alertCard: { background:'rgba(226,75,74,.08)', border:'0.5px solid rgba(226,75,74,.3)', borderRadius: 10, padding:'10px 12px', margin:'10px 12px 0', display:'flex', gap: 10, alignItems:'center' },
  statsGrid: { padding:'12px', display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap: 8 },
  statCard: { background:'#fff', border:`0.5px solid ${C.line}`, borderRadius: 10, padding: 10 },

  installBanner: { display:'flex', alignItems:'center', gap: 8, padding:'8px 12px', margin:'6px 10px 10px', background:'rgba(0,168,181,.25)', border:'0.5px solid rgba(255,255,255,.25)', borderRadius: 10, color:'#fff', fontSize: 12, cursor:'pointer' },
  installBtnInline: { background:'#fff', color: C.navy, fontSize: 11, padding:'3px 9px', borderRadius: 999, fontWeight: 600, whiteSpace:'nowrap' },
}
